import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { User } from 'lucide-react';
import { useVoiceEngine } from '@/hooks/useVoiceEngine';
import { getJapaneseScenarioByCode } from '@/data/insurance-scenarios-jp';
import { type InsuranceProduct } from '@/data/insurance-products';
import {
  getProductRecommendations,
  type ProductComparison,
} from '@/services/product-recommendations';
import { ClientProfilePanel } from '../roleplay/components/ClientProfilePanel';
import { ProductRecommendationsPanel } from '../roleplay/components/ProductRecommendationsPanel';
import { ConversationPanel } from '../roleplay/components/ConversationPanel';
import {
  InlineRealtimeFeedback,
  type FeedbackTip,
} from '../roleplay/components/InlineRealtimeFeedback';
import { InsuranceFeedbackAnalysis } from '../roleplay/components/InsuranceFeedbackAnalysis';
import { LoadingOverlay } from '@/components/ui/loading';
import { ErrorMessage } from '@/components/loading-error';
import api from '@/utils/ky';

type LoadingState =
  | 'initial'
  | 'loading'
  | 'ready'
  | 'active'
  | 'error'
  | 'connecting'
  | 'ending';

export function JapaneseInsuranceRoleplay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scenarioCode = searchParams.get('scenario');
  const language = searchParams.get('lang') || 'ja';

  // Engine selection: default to elevenlabs, allow livekit override
  const engineParam = searchParams.get('engine') as 'elevenlabs' | 'livekit' | null;
  const selectedEngine: 'elevenlabs' | 'livekit' =
    engineParam === 'livekit' ? 'livekit' : 'elevenlabs';

  // API version for recommendations
  const apiVersion = parseInt(searchParams.get('version') || '6', 10);

  const { t, i18n } = useTranslation();

  // State
  const [loadingState, setLoadingState] = useState<LoadingState>('initial');
  const [error, setError] = useState<string | null>(null);
  const [isPracticeStarted, setIsPracticeStarted] = useState(false);
  const [transcript, setTranscript] = useState<
    Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);
  const [recommendedProducts, setRecommendedProducts] = useState<
    InsuranceProduct[]
  >([]);
  const [productComparisons, setProductComparisons] = useState<
    ProductComparison[]
  >([]); // NEW: For comparison mode
  const [responseMode, setResponseMode] = useState<
    'no_action' | 'recommend' | 'compare'
  >('no_action'); // NEW: Track mode
  const [isClientSpeaking, setIsClientSpeaking] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(true);
  const [showHintToast, setShowHintToast] = useState(false);
  const [feedbackTips, setFeedbackTips] = useState<FeedbackTip[]>([]); // Real-time feedback tips
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [mobileModalOpen, setMobileModalOpen] = useState<
    'none' | 'client' | 'products'
  >('none');

  // Voice engine
  const voiceEngine = useVoiceEngine(language, selectedEngine);
  const { status, isSpeaking: isAISpeaking } = voiceEngine;

  // Refs
  const startTimeRef = useRef<number>(0);
  const voiceEngineRef = useRef(voiceEngine);
  const pendingRequestRef = useRef<boolean>(false);
  // Buffer for AI messages that arrive before user transcript (ElevenLabs ordering)
  const bufferedAiMessageRef = useRef<{ message: string; timestamp: Date } | null>(null);
  const bufferFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load scenario
  const scenario = useMemo(() => {
    if (!scenarioCode) return null;
    return getJapaneseScenarioByCode(scenarioCode);
  }, [scenarioCode]);

  // Update voice engine ref
  useEffect(() => {
    voiceEngineRef.current = voiceEngine;
  }, [voiceEngine]);

  // Auto-collapse left panel on mobile and close modals on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        // lg breakpoint
        setIsLeftPanelCollapsed(true);
        setMobileModalOpen('none');
      }
    };

    // Set initial state
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile modals on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileModalOpen !== 'none') {
        setMobileModalOpen('none');
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileModalOpen]);

  // Prevent body scroll when mobile modals are open
  useEffect(() => {
    if (mobileModalOpen !== 'none') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileModalOpen]);

  // Set language
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Redirect if no scenario
  useEffect(() => {
    if (!scenarioCode || !scenario) {
      console.error('No scenario code provided or scenario not found');
      navigate('/');
      return;
    }
    setLoadingState('ready');
  }, [scenarioCode, scenario, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (bufferFlushTimerRef.current) {
        clearTimeout(bufferFlushTimerRef.current);
      }
      const currentEngine = voiceEngineRef.current;
      if (
        currentEngine &&
        (currentEngine.status === 'connected' ||
          currentEngine.status === 'connecting')
      ) {
        console.log('🧹 Cleaning up voice engine on unmount');
        currentEngine.stop().catch((err) => {
          console.error('Error stopping voice engine during cleanup:', err);
        });
      }
    };
  }, []);

  // Show hint toast when practice starts (only once)
  useEffect(() => {
    if (isPracticeStarted && isLeftPanelCollapsed) {
      // Show toast after a short delay (1 second after practice starts)
      const showTimer = setTimeout(() => {
        setShowHintToast(true);
      }, 1000);

      // Auto-hide after 4 seconds
      const hideTimer = setTimeout(() => {
        setShowHintToast(false);
      }, 5000);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isPracticeStarted, isLeftPanelCollapsed]);

  // Real-time product recommendation using LlamaIndex API
  // Prevents duplicate requests by waiting for current request to complete
  const updateProductRecommendations = async (
    latestTranscript: typeof transcript,
  ) => {
    console.log('---scenario:', scenario, 'latestTranscript', latestTranscript);
    if (!scenario || latestTranscript.length === 0) {
      setRecommendedProducts([]);
      return;
    }

    console.log('------pendingRequestRef.current', pendingRequestRef.current);

    // If a request is already in progress, skip this call
    // Let the current request finish naturally (no cancellation)
    if (pendingRequestRef.current) {
      console.log(
        '⏭️ Request already in progress, skipping new request (will wait for current to finish)',
      );
      return;
    }

    // Mark request as in progress
    pendingRequestRef.current = true;

    try {
      console.log('🚀 Starting product recommendation request');

      // Call the backend API for recommendations
      const response = await getProductRecommendations({
        scenarioCode: scenarioCode!,
        conversationHistory: latestTranscript.map((msg) => ({
          source: msg.source,
          message: msg.message,
          timestamp: msg.timestamp.toISOString(),
        })),
        clientPersona: {
          age: scenario.clientPersona.age,
          occupation: scenario.clientPersona.occupation,
          concerns: scenario.clientPersona.concerns,
        },
        version: apiVersion, // Pass version from URL parameter
      });
      console.log('-------response:', response);

      // Handle response based on mode
      setResponseMode(response.mode);

      if (response.mode === 'no_action') {
        // Mode A: No action needed - keep existing data
        console.log('ℹ️ No action mode - keeping existing data');
      } else if (response.mode === 'recommend') {
        // Mode B: Product recommendations
        if (response.shouldUpdate && response.products.length > 0) {
          console.log('✅ Received product recommendations:', {
            count: response.products.length,
            concerns: response.analysisMetadata.detectedConcerns,
            context: response.analysisMetadata.conversationContext,
          });

          // Update with top 5 products from API
          setRecommendedProducts(response.products.slice(0, 5));
          setProductComparisons([]); // Clear comparisons when in recommend mode
        } else {
          console.log(
            'ℹ️ No products recommended (shouldUpdate=false or empty products)',
          );
          setRecommendedProducts([]);
        }
      } else if (response.mode === 'compare') {
        // Mode C: Product comparisons
        if (response.comparisons && response.comparisons.length > 0) {
          console.log('✅ Received product comparisons:', {
            count: response.comparisons.length,
            competitors: response.analysisMetadata.detectedCompetitors,
            context: response.analysisMetadata.conversationContext,
          });

          setProductComparisons(response.comparisons);
          setRecommendedProducts([]); // Clear products when in compare mode
        } else {
          console.log('ℹ️ No comparisons generated');
          setProductComparisons([]);
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch product recommendations:', error);

      // IMPORTANT: On error, KEEP previous recommendations (as specified)
      // The backend returns HTTP 500 with { "error": "message" } on errors
      // So we don't clear the recommendations - user keeps seeing last valid set
      console.log('⚠️ Keeping previous recommendations due to API error');

      // You could optionally show a subtle toast notification here
      // to let the user know the update failed (but don't disrupt UX)
    } finally {
      // Clear pending state
      pendingRequestRef.current = false;
      console.log('✅ Request completed, ready for next update');
    }
  };

  // Handle start practice
  const handleStartPractice = async () => {
    if (loadingState === 'connecting' || !scenario) {
      console.log('⚠️ Already connecting or no scenario, ignoring click');
      return;
    }

    try {
      setLoadingState('connecting');
      console.log('🚀 Starting Japanese insurance practice session:', {
        scenarioCode,
        language,
        agentId: scenario.agentId,
        voiceId: scenario.clientPersona.voiceId,
      });

      // Request microphone access
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('🎤 Microphone access granted');
      } catch (micError) {
        console.error('🎤 Microphone access error:', micError);
        throw new Error('MICROPHONE_DENIED');
      }

      // Japanese-specific number formatting rules for TTS pronunciation
      const getJapaneseNumberRules = () => {
        return `[重要 — 数字の読み方ルール]

音声で話す場合、すべての数字を日本語テキスト形式に変換してください：
- "2,800円" → "二千八百円"
- "5,000円" → "五千円"
- "200,000円" → "二十万円"
- "2,000万円" → "二千万円"
- "420万円" → "四百二十万円"
- "80万円" → "八十万円"
- "26歳" → "にじゅうろくさい"
- "2週間" → "にしゅうかん"

アラビア数字は使わないでください。

`;
      };

      // Generate scenario-specific voice prompt
      const getVoicePrompt = () => {
        return `## あなたの役割
あなたは${scenario.clientPersona.nameKo}（たなか ゆうと）、${scenario.clientPersona.age}歳のシステムエンジニアです。
中堅IT企業に勤務する入社2年目の社会人です。独身で、江東区の1Kアパートに一人暮らしです。

## あなたの状況
同僚に勧められて保険の相談に来ました。保険には全く加入しておらず、知識もほとんどありません。
会社の健康保険だけに入っています。貯蓄は約八十万円です。
保険に否定的ではありませんが、まだ若くて健康なので緊急性は感じていません。

## あなたの内面の動機（自分からは言わない）
- 先輩が最近入院して、自己負担が結構かかったと聞いて気になっている
- 社会人になったら保険に入るべきだと親にも言われている
- でも具体的に何が必要かはわからない

## 重要：あなたは顧客です
あなたはお客様です。相手はアドバイザー（営業担当者）です。
- 「どのようなご相談ですか？」とは絶対に言わない
- 「お手伝いできますか？」とは絶対に言わない
- アドバイスや商品の説明はしない
- あなたは相談を受ける側、相手が説明する側

## 会話の流れ
1. 最初の挨拶：「${scenario.clientPersona.initialMessageKo}」と言う
2. アドバイザーが仕事や生活について聞いてきたら、短く答える
3. 信頼関係ができてきたら、先輩の入院の話を打ち明ける：「実は…先輩が最近入院して、二週間くらいだったんですけど、自己負担が結構かかったって聞いて、ちょっと気になってました。」
4. 商品説明を聞いたら具体的な質問をする：「月、二千八百円ですか…思ったより安いですね。」「先進医療って、具体的にはどういうものなんですか？」
5. 最後は持ち帰りを希望：「うーん、内容はわかったんですけど、すぐには決められないです。ちょっと資料を持ち帰って考えてもいいですか？」「親にも一応相談してみたいので。」

## 話し方のスタイル
- 必ず「です・ます」調で話す
- 短い文章で答える（1〜2文）
- 聞かれたことだけに答える — 余計な情報は言わない
- 自然な間投詞を使う：「あの…」「えっと…」「うーん…」「そうですね…」
- 控えめで慎重 — 自分の意見を強く主張しない
- 直接「いいえ」とは言わず、遠回しに

## 背景情報（聞かれた時だけ答える）
- 職業：IT企業のシステムエンジニア、二年目
- 住まい：江東区の1Kアパート、一人暮らし
- 保険：会社の健康保険だけ
- 年収：約四百二十万円（聞かれたら）
- 貯蓄：約八十万円（聞かれたら）

## 絶対にやってはいけないこと
❌ アドバイザーの役割を演じない
❌ 商品を推薦しない
❌ 保険の概念を説明しない
❌ 専門家のように話さない
❌ 自分から情報を積極的に提供しない

日本語のみで話してください。`;
      };

      const voiceConfig = selectedEngine === 'livekit'
        ? {
            language,
            voiceId: 'Japanese_OptimisticYouth',
            prompt: `${getJapaneseNumberRules()}${getVoicePrompt()}`,
            firstMessage: scenario.clientPersona.initialMessageKo,
            roomName: `haruka-${Date.now()}`,
            participantName: `user-${Date.now()}`,
          }
        : {
            agentId: scenario.agentId,
            language,
            voiceId: scenario.clientPersona.voiceId,
            prompt: `${getJapaneseNumberRules()}${getVoicePrompt()}`,
            firstMessage: scenario.clientPersona.initialMessageKo,
          };

      console.log('🔌 Initiating voice engine connection');

      const conversationId = await voiceEngine.start(voiceConfig, {
        onConnect: () => {
          console.log('✅ Successfully connected to voice engine');
          startTimeRef.current = Date.now();
          setLoadingState('active');
          setIsPracticeStarted(true);
        },
        onError: (error) => {
          console.error('❌ Voice engine connection error:', error);
          setError(error?.message || error || 'Connection Error');
          setLoadingState('error');
        },
        onMessage: ({ source, message }) => {
          console.log('💬 Message received:', { source, message });

          // ElevenLabs sends agent_response BEFORE user_transcript.
          // To avoid a visual jerk (AI message appearing then shifting down),
          // buffer the AI message when the user transcript hasn't arrived yet,
          // then flush both in correct order when the transcript comes in.

          if (source === 'ai') {
            setTranscript((prev) => {
              // If last message is also AI, a user message is expected in between
              // but hasn't arrived yet — buffer this AI message.
              if (prev.length > 0 && prev[prev.length - 1].source === 'ai') {
                bufferedAiMessageRef.current = { message, timestamp: new Date() };

                // Safety fallback: flush after 5s if user transcript never arrives
                if (bufferFlushTimerRef.current) clearTimeout(bufferFlushTimerRef.current);
                bufferFlushTimerRef.current = setTimeout(() => {
                  const buffered = bufferedAiMessageRef.current;
                  if (buffered) {
                    bufferedAiMessageRef.current = null;
                    setTranscript((p) => [
                      ...p,
                      { source: 'ai' as const, message: buffered.message, timestamp: buffered.timestamp },
                    ]);
                  }
                }, 5000);

                return prev; // Don't add yet
              }

              const newTranscript = [
                ...prev,
                { source, message, timestamp: new Date() },
              ];

              // Update product recommendations
              const currentAiCount =
                prev.filter((m) => m.source === 'ai').length + 1;
              if (currentAiCount >= 2) {
                console.log(
                  '🔄 Updating product recommendations (client message #' +
                    currentAiCount +
                    ')',
                );
                setIsLoadingProducts(true);
                const minDisplayTime = 1500 + Math.random() * 1000;
                const startTime = Date.now();
                updateProductRecommendations(newTranscript).finally(() => {
                  const elapsed = Date.now() - startTime;
                  const remainingTime = Math.max(0, minDisplayTime - elapsed);
                  setTimeout(() => {
                    setIsLoadingProducts(false);
                  }, remainingTime);
                });
              }

              return newTranscript;
            });
          } else {
            // User transcript arrived — add it, then flush any buffered AI message
            if (bufferFlushTimerRef.current) {
              clearTimeout(bufferFlushTimerRef.current);
              bufferFlushTimerRef.current = null;
            }
            const buffered = bufferedAiMessageRef.current;
            bufferedAiMessageRef.current = null;

            setTranscript((prev) => {
              const newTranscript = [
                ...prev,
                { source, message, timestamp: new Date() },
                ...(buffered
                  ? [{ source: 'ai' as const, message: buffered.message, timestamp: buffered.timestamp }]
                  : []),
              ];

              // If we flushed a buffered AI message, trigger product recommendations
              if (buffered) {
                const currentAiCount = newTranscript.filter((m) => m.source === 'ai').length;
                if (currentAiCount >= 2) {
                  console.log(
                    '🔄 Updating product recommendations (client message #' +
                      currentAiCount +
                      ')',
                  );
                  setIsLoadingProducts(true);
                  const minDisplayTime = 1500 + Math.random() * 1000;
                  const startTime = Date.now();
                  updateProductRecommendations(newTranscript).finally(() => {
                    const elapsed = Date.now() - startTime;
                    const remainingTime = Math.max(0, minDisplayTime - elapsed);
                    setTimeout(() => {
                      setIsLoadingProducts(false);
                    }, remainingTime);
                  });
                }
              }

              return newTranscript;
            });
          }
        },
        onAgentSpeaking: (speaking) => {
          setIsClientSpeaking(speaking);
        },
      });

      console.log('🎯 Conversation started successfully:', conversationId);
    } catch (error) {
      console.error('💥 Error starting practice:', error);
      setError(
        error?.message === 'MICROPHONE_DENIED'
          ? 'マイク権限が必要です。ブラウザの設定でマイク権限を許可してください。'
          : '練習セッションを開始できません。もう一度お試しください。',
      );
      setLoadingState('error');
    }
  };

  // Handle end practice
  const handleEndPractice = async () => {
    try {
      setLoadingState('ending');
      console.log('🛑 Ending practice session...');

      // Clear pending state (let any in-flight request complete naturally)
      pendingRequestRef.current = false;

      // Stop voice engine
      if (voiceEngine) {
        await voiceEngine.stop();
      }

      // Calculate session duration
      const duration = (Date.now() - startTimeRef.current) / 1000;
      console.log('📊 Session duration:', duration, 'seconds');

      // Format transcript for API
      const formattedTranscript = transcript
        .map(
          (msg) =>
            `${msg.source === 'ai' ? 'Client' : 'Salesperson'}: ${msg.message}`,
        )
        .join('\n');

      // Generate feedback using API
      try {
        console.log('🔄 Requesting post-session feedback...');

        // Note: api already has prefixUrl configured, so just use relative path
        const feedback = await api
          .post('api/roleplay/feedback/session-analysis', {
            json: {
              scenarioCode,
              transcript: formattedTranscript,
              duration,
              language,
            },
            timeout: 90000, // 90 second timeout (same as Prudential)
          })
          .json();

        console.log('✅ Feedback received:', feedback);
        setFeedbackData(feedback);
        setShowFeedback(true);
      } catch (error) {
        console.error('❌ Failed to generate feedback:', error);
        // Show error but still allow user to see transcript
        setError(
          'フィードバックの生成に失敗しました。セッションは正常に終了しました。',
        );
      }

      setIsPracticeStarted(false);
      setLoadingState('ready');
    } catch (error) {
      console.error('Error ending practice:', error);
      setLoadingState('error');
      setError('セッション終了中にエラーが発生しました。');
    }
  };

  // Timer state for session duration
  const [sessionDuration, setSessionDuration] = useState(0);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPracticeStarted && loadingState === 'active') {
      interval = setInterval(() => {
        setSessionDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setSessionDuration(0);
    }
    return () => clearInterval(interval);
  }, [isPracticeStarted, loadingState]);

  // Format duration mm:ss
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  if (!scenario) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-100 max-w-md mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            シナリオが見つかりません
          </h1>
          <p className="text-gray-600">無効なシナリオコードです。</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC]">
      {/* Header - Modern Glassmorphism */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/80 px-3 md:px-6 py-2 md:py-3 shadow-sm transition-all duration-300">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="md:w-5 md:h-5"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-sm md:text-lg font-bold text-gray-900 tracking-tight">
                {t('insurance.title')}
              </h1>
              <p className="text-[10px] md:text-xs text-gray-500 font-medium">
                保険セールス ロールプレイ
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {isPracticeStarted && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full border border-gray-200">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-mono font-medium text-gray-700">
                  {formatDuration(sessionDuration)}
                </span>
              </div>
            )}

            {isPracticeStarted && (
              <button
                onClick={handleEndPractice}
                className="group flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white border border-red-200 text-red-600 text-xs md:text-sm rounded-lg md:rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 font-semibold shadow-sm hover:shadow"
              >
                <span className="hidden sm:inline">
                  {t('insurance.endSession')}
                </span>
                <span className="sm:hidden"> {t('insurance.endSession')}</span>
                <svg
                  className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:translate-x-0.5 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 overflow-hidden relative p-2 sm:p-4 md:p-6 pb-24 lg:pb-6">
        {/* Hint Toast - Elegant disappearing hint */}
        <AnimatePresence>
          {showHintToast && (
            <motion.div
              initial={{ opacity: 0, x: -20, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{
                duration: 0.4,
                type: 'spring',
                stiffness: 400,
                damping: 30,
              }}
              className="absolute top-4 left-4 md:top-8 md:left-8 z-50 bg-gray-900/95 backdrop-blur text-white pl-3 pr-8 md:pl-4 md:pr-10 py-2.5 md:py-3.5 rounded-xl md:rounded-2xl shadow-2xl border border-white/10 flex items-center gap-2 md:gap-3 max-w-[calc(100vw-2rem)] sm:max-w-sm"
            >
              <div className="bg-blue-500/20 text-blue-400 rounded-full p-1.5 md:p-2">
                <User className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs md:text-sm font-bold mb-0.5 text-white truncate">
                  顧客情報パネル
                </div>
                <div className="text-[10px] md:text-xs text-gray-300 leading-relaxed">
                  左矢印をクリックして顧客情報を確認してください
                </div>
              </div>
              <button
                onClick={() => setShowHintToast(false)}
                className="absolute top-1.5 right-1.5 md:top-2 md:right-2 p-0.5 md:p-1 text-white/40 hover:text-white transition-colors rounded-full hover:bg-white/10"
                aria-label="Dismiss"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="md:w-3.5 md:h-3.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Floating Action Buttons */}
        <div className="lg:hidden fixed bottom-6 left-0 right-0 z-30 flex justify-center gap-3 px-4 pointer-events-none">
          <button
            onClick={() => setMobileModalOpen('client')}
            className="pointer-events-auto flex items-center gap-2 px-4 py-3 bg-white border-2 border-blue-500 text-blue-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
          >
            <User className="w-4 h-4" />
            <span className="text-sm">顧客情報</span>
          </button>
          <button
            onClick={() => setMobileModalOpen('products')}
            className="pointer-events-auto flex items-center gap-2 px-4 py-3 bg-white border-2 border-purple-500 text-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <span className="text-sm">おすすめ商品</span>
          </button>
        </div>

        <div className="max-w-[1800px] mx-auto h-full flex gap-2 sm:gap-4 md:gap-6">
          {/* Left Panel - Client Profile (Desktop: Collapsible Panel, Mobile: Modal) */}
          <div className="hidden lg:block flex-shrink-0 h-full transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1.0)]">
            <ClientProfilePanel
              persona={scenario.clientPersona}
              language={language as 'en' | 'ko' | 'ja'}
              isCollapsed={isLeftPanelCollapsed}
              onToggle={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
            />
          </div>

          {/* Mobile Client Profile Modal */}
          <AnimatePresence>
            {mobileModalOpen === 'client' && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setMobileModalOpen('none')}
                  className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                />
                {/* Modal */}
                <motion.div
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -100, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="lg:hidden fixed left-0 top-0 h-screen w-[90vw] max-w-md z-50 flex flex-col"
                >
                  <div className="flex-1 flex flex-col bg-white rounded-r-2xl shadow-2xl overflow-hidden">
                    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                      <h2 className="font-bold text-gray-900">顧客情報</h2>
                      <button
                        onClick={() => setMobileModalOpen('none')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      <ClientProfilePanel
                        persona={scenario.clientPersona}
                        language={language as 'en' | 'ko' | 'ja'}
                        isCollapsed={false}
                        onToggle={() => {}}
                        showToggle={false}
                        fullWidth={true}
                      />
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Center Panel - Conversation */}
          <div className="flex-1 flex flex-col min-w-0 h-full shadow-sm rounded-2xl overflow-hidden bg-white border border-gray-200/60">
            {/* Real-time Feedback Analyzer - runs in background */}
            {isPracticeStarted && scenarioCode && (
              <InlineRealtimeFeedback
                transcript={transcript}
                scenarioCode={scenarioCode}
                language={language as 'en' | 'ko' | 'ja'}
                setFeedbackTips={setFeedbackTips}
              />
            )}

            <ConversationPanel
              transcript={transcript}
              isAISpeaking={isClientSpeaking}
              persona={scenario.clientPersona}
              isPracticeStarted={isPracticeStarted}
              onStartPractice={handleStartPractice}
              isConnecting={loadingState === 'connecting'}
              language={language as 'en' | 'ko' | 'ja'}
              feedbackTips={feedbackTips}
            />
          </div>

          {/* Right Panel - Product Recommendations / Comparisons (Desktop: Panel, Mobile: Modal) */}
          <div className="hidden lg:block w-80 flex-shrink-0 h-full">
            <ProductRecommendationsPanel
              products={recommendedProducts}
              comparisons={productComparisons}
              responseMode={responseMode}
              isPracticeStarted={isPracticeStarted}
              isUpdating={isLoadingProducts}
              language={language as 'en' | 'ko' | 'ja'}
            />
          </div>

          {/* Mobile Product Recommendations Modal */}
          <AnimatePresence>
            {mobileModalOpen === 'products' && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setMobileModalOpen('none')}
                  className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                />
                {/* Modal */}
                <motion.div
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 100, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="lg:hidden fixed right-0 top-0 h-screen w-[90vw] max-w-md z-50 flex flex-col"
                >
                  <div className="flex-1 flex flex-col bg-white rounded-l-2xl shadow-2xl overflow-hidden">
                    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                      <h2 className="font-bold text-gray-900">おすすめ商品</h2>
                      <button
                        onClick={() => setMobileModalOpen('none')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      <ProductRecommendationsPanel
                        products={recommendedProducts}
                        comparisons={productComparisons}
                        responseMode={responseMode}
                        isPracticeStarted={isPracticeStarted}
                        isUpdating={isLoadingProducts}
                        language={language as 'en' | 'ko' | 'ja'}
                      />
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Loading States */}
      <AnimatePresence>
        {loadingState !== 'ready' && loadingState !== 'active' && (
          <LoadingOverlay state={loadingState} />
        )}
      </AnimatePresence>

      {/* Error Message */}
      {error && (
        <ErrorMessage
          message={error}
          onRetry={() => {
            setError(null);
            setLoadingState('ready');
          }}
        />
      )}

      {/* Post-Session Feedback Modal */}
      <AnimatePresence>
        {showFeedback && feedbackData && scenario && (
          <InsuranceFeedbackAnalysis
            feedbackData={feedbackData}
            language={language as 'en' | 'ko' | 'ja'}
            onClose={() => {
              setShowFeedback(false);
              // Clear session data
              setTranscript([]);
              setRecommendedProducts([]);
              setFeedbackTips([]);
              setFeedbackData(null);
            }}
            clientName={
              scenario.clientPersona.nameKo || scenario.clientPersona.name
            }
            duration={(Date.now() - startTimeRef.current) / 1000}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
